/**
 * CustomerSelectorInput - Input container with customer chips and textarea
 */

import React, { useEffect } from "react";

import { Cancel } from "@mui/icons-material";
import { useTheme } from "@mui/material";

import { CustomerChip } from "./CustomerChip";
import { CustomerDropdown } from "./CustomerDropdown";

import type { CustomerSelectionItem } from "../types";

interface CustomerSelectorInputProps {
  selectedItems: CustomerSelectionItem[];
  inputValue: string;
  isOpen: boolean;
  emailError: string;
  containerRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  filteredCustomers: CustomerSelectionItem[];
  selectedIds: Set<string>;
  onInputChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  onRemoveItem: (item: CustomerSelectionItem) => void;
  onClearAll: () => void;
  onFocus: () => void;
  onContainerClick: () => void;
  onContainerKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onSelectItem: (item: CustomerSelectionItem) => void;
  getCustomerKey: (item: CustomerSelectionItem) => string;
  autoFocus?: boolean;
}

export function CustomerSelectorInput({
  selectedItems,
  inputValue,
  isOpen,
  emailError,
  containerRef,
  inputRef,
  filteredCustomers,
  selectedIds,
  onInputChange,
  onKeyDown,
  onPaste,
  onRemoveItem,
  onClearAll,
  onFocus,
  onContainerClick,
  onContainerKeyDown,
  onSelectItem,
  getCustomerKey,
  autoFocus = false,
}: CustomerSelectorInputProps) {
  const theme = useTheme();

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus, inputRef]);
  // Auto-resize textarea to fit content
  const autoResize = () => {
    if (inputRef.current) {
      const element = inputRef.current;
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onInputChange(e.target.value);
    autoResize();
  };

  return (
    <div ref={containerRef} style={{ position: "relative", flex: 1 }}>
      {/* Input container */}
      <div
        role="button"
        tabIndex={0}
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "start",
          gap: "8px",
          padding: "12px",
          paddingRight: "40px",
          cursor: "text",
          transition: "all 0.2s",
          borderRadius: "4px",
          backgroundColor: theme.palette.background.paper,
        }}
        onClick={onContainerClick}
        onKeyDown={onContainerKeyDown}
      >
        {/* Selected chips */}
        {selectedItems.map((item) => (
          <CustomerChip
            key={getCustomerKey(item)}
            item={item}
            onRemove={onRemoveItem}
            getCustomerKey={getCustomerKey}
          />
        ))}

        {/* Text input - full width so it always starts on a new row */}
        <textarea
          ref={inputRef}
          value={inputValue}
          rows={1}
          onChange={handleInputChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={
            selectedItems.length === 0
              ? "Search for customers or type/paste in email addresses..."
              : ""
          }
          style={{
            outline: "none",
            fontSize: "14px",
            color: "black",
            backgroundColor: "transparent",
            padding: "4px 0",
            fontFamily: "Quicksand, sans-serif",
            width: "100%",
            resize: "none",
            overflow: "hidden",
            lineHeight: "1.4",
            border: "none",
          }}
        />
      </div>

      {/* Email validation error */}
      {emailError && (
        <div
          style={{
            fontSize: "12px",
            marginTop: "4px",
            marginLeft: "4px",
            color: theme.palette.error.main,
            fontFamily: "Quicksand, sans-serif",
          }}
        >
          {emailError}
        </div>
      )}

      {/* Clear all button */}
      {selectedItems.length > 0 && (
        <button
          type="button"
          aria-label="Clear all customers"
          onClick={(e) => {
            e.stopPropagation();
            onClearAll();
          }}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            padding: "4px",
            borderRadius: "50%",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: theme.palette.text.secondary,
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.palette.action.hover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <Cancel
            sx={{
              fontSize: 20,
              color: "action.disabled",
            }}
          />
        </button>
      )}

      {/* Dropdown */}
      <CustomerDropdown
        isOpen={isOpen}
        filteredCustomers={filteredCustomers}
        selectedIds={selectedIds}
        onSelectItem={onSelectItem}
        getCustomerKey={getCustomerKey}
      />
    </div>
  );
}
